/**
 * ManualPaymentModal - 调整 uncompleted 任务的司机支付金额
 *
 * 在 Reassign 操作完成后弹出，加载现有 draft payment(s)，
 * 允许 dispatcher 调整原司机的支付金额。
 *
 * - 单 payment（company driver + company truck）: 1 个输入框
 * - 双 payment（company driver + owner truck）: 2 个输入框
 */

import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X, DollarSign, FileText, User, Package, Loader2, Truck } from 'lucide-react'
import api from '../../../services/api'

export interface ManualPaymentData {
  amount: number
  notes?: string
}

interface DraftPayment {
  id: number
  payment_number: string
  payment_role: 'driver' | 'truck_owner'
  base_rate: number
  extra_rate: number
  total_amount: number
  driver_name: string | null
  driver_type: string | null
  status: string
}

interface ManualPaymentModalProps {
  open: boolean
  onClose: () => void
  onConfirm: (data: ManualPaymentData) => void
  isSaving?: boolean
  taskId: string | number
  taskBusinessId?: string
  driverName?: string
  containerNumber?: string
}

export const ManualPaymentModal: React.FC<ManualPaymentModalProps> = ({
  open,
  onClose,
  onConfirm,
  isSaving = false,
  taskId,
  taskBusinessId,
  driverName,
  containerNumber,
}) => {
  const [drafts, setDrafts] = useState<DraftPayment[]>([])
  const [loading, setLoading] = useState(true)
  const [driverAmount, setDriverAmount] = useState<string>('')
  const [ownerAmount, setOwnerAmount] = useState<string>('')
  const [notes, setNotes] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load existing draft payments when modal opens
  useEffect(() => {
    if (!open || !taskId) return
    setLoading(true)
    setError(null)
    const numericId = typeof taskId === 'string' ? parseInt(taskId) : taskId
    api.get(`/draft-driver-payments/task/${numericId}`)
      .then(res => {
        const data = (res.data || []) as DraftPayment[]
        setDrafts(data)
        const driverDraft = data.find(d => d.payment_role === 'driver')
        const ownerDraft = data.find(d => d.payment_role === 'truck_owner')
        if (driverDraft) setDriverAmount(String(driverDraft.base_rate || 0))
        if (ownerDraft) setOwnerAmount(String(ownerDraft.base_rate || 0))
      })
      .catch(() => setDrafts([]))
      .finally(() => setLoading(false))
  }, [open, taskId])

  if (!open) return null

  const driverDraft = drafts.find(d => d.payment_role === 'driver')
  const ownerDraft = drafts.find(d => d.payment_role === 'truck_owner')
  const isDual = !!ownerDraft
  const driverType = driverDraft?.driver_type || 'Company'

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      // Adjust driver payment
      if (driverDraft) {
        const amt = parseFloat(driverAmount)
        if (!isNaN(amt) && amt >= 0) {
          await api.patch(`/draft-driver-payments/${driverDraft.id}/base-rate`, { base_rate: amt })
        }
      }
      // Adjust truck owner payment
      if (ownerDraft) {
        const amt = parseFloat(ownerAmount)
        if (!isNaN(amt) && amt >= 0) {
          await api.patch(`/draft-driver-payments/${ownerDraft.id}/base-rate`, { base_rate: amt })
        }
      }
      // Confirm draft → pending so payment enters approval workflow
      const firstDraft = driverDraft || ownerDraft
      if (firstDraft && firstDraft.status === 'draft') {
        await api.post(`/draft-driver-payments/${firstDraft.id}/confirm`).catch(() => {
          console.warn('Failed to confirm payment after manual adjustment')
        })
      }
      // Call original onConfirm for any additional logic (e.g. toast)
      onConfirm({ amount: parseFloat(driverAmount) || 0, notes: notes.trim() || undefined })
    } catch (err) {
      console.error('Failed to adjust payment:', err)
      setError('Failed to save payment adjustment')
    } finally {
      setSaving(false)
    }
  }

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-amber-50 to-orange-50">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-4 h-4 text-amber-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Adjust Driver Payment</h3>
              <p className="text-xs text-gray-500">Set payment for uncompleted task</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          {/* Task Info */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="w-4 h-4 text-gray-400" />
              <span className="text-gray-500">Task:</span>
              <span className="font-medium text-gray-900">{taskBusinessId || taskId}</span>
            </div>
            {driverName && (
              <div className="flex items-center gap-2 text-sm">
                <User className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Driver:</span>
                <span className="font-medium text-gray-900">{driverName}</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{driverType}</span>
              </div>
            )}
            {containerNumber && (
              <div className="flex items-center gap-2 text-sm">
                <Package className="w-4 h-4 text-gray-400" />
                <span className="text-gray-500">Container:</span>
                <span className="font-medium text-gray-900">{containerNumber}</span>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              <span className="ml-2 text-sm text-gray-500">Loading payments...</span>
            </div>
          ) : drafts.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No draft payments found for this task.</p>
          ) : (
            <>
              {/* Driver Payment */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <div className="flex items-center gap-1.5">
                    <User className="w-3.5 h-3.5" />
                    Driver Payment
                    <span className="text-red-500">*</span>
                  </div>
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={driverAmount}
                    onChange={(e) => setDriverAmount(e.target.value)}
                    className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Truck Owner Payment (only for dual payment) */}
              {isDual && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <div className="flex items-center gap-1.5">
                      <Truck className="w-3.5 h-3.5" />
                      Truck Owner Payment
                      <span className="text-red-500">*</span>
                    </div>
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={ownerAmount}
                      onChange={(e) => setOwnerAmount(e.target.value)}
                      className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm"
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 text-sm resize-none"
                  placeholder="Payment reason or notes..."
                />
              </div>
            </>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Skip
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || drafts.length === 0}
            className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors flex items-center gap-2"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Saving...' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  )

  return createPortal(modal, document.body)
}
